import { useMemo, useState } from 'react';
import {
  Card,
  Checkbox,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { ChevronDown, ListFilter, Upload, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { SellerTagType } from '@/lib/types/feature/seller-tag';
import { SelectColumnFilter, TextColumnFilter, type FilterOption } from './ColumnFilter';

/** 一覧に出す店舗1件 */
export interface AssignTableSeller {
  /** 店舗ID（内部ID）。選択・付与の指定に使う */
  sellerId: string;
  /** 店舗ID（顧客側）。表示と絞り込みに使う */
  sellerCustomerId: string;
  sellerName: string;
  hierarchy: string;
}

interface SellerTagAssignTableProps {
  sellers: AssignTableSeller[];
  tagTypes: SellerTagType[];
  /** 編集対象のタグ種別 */
  selectedTypeId: string;
  /** 店舗の内部ID → (タグ種別ID → タグ名ID) */
  assignments: Map<string, Map<string, string>>;
  onAssign: (sellerIds: string[], tagId: string) => void;
  onUnassign: (sellerIds: string[]) => void;
  onOpenImport: () => void;
  submitting: boolean;
}

/** タグ列の絞り込みで「未設定」を表す値 */
const UNSET = '__unset__';

/** テキストで絞り込む列 */
type TextColumn = 'sellerCustomerId' | 'hierarchy' | 'sellerName';

const TEXT_COLUMNS: TextColumn[] = ['sellerCustomerId', 'hierarchy', 'sellerName'];

const TEXT_COLUMN_LABELS: Record<TextColumn, string> = {
  sellerCustomerId: '店舗ID',
  hierarchy: '階層',
  sellerName: '店舗名',
};

const TEXT_COLUMN_WIDTHS: Record<TextColumn, number> = {
  sellerCustomerId: 110,
  hierarchy: 200,
  sellerName: 150,
};

/**
 * タグ種別の列幅。編集対象を切り替えても列の幅が動かないよう、すべての種別で固定する
 * （編集中かどうかは色だけで示す）
 */
const TAG_COLUMN_WIDTH = 170;

/**
 * 店舗一覧とタグの付与（FKV3-13002）
 *
 * タグ種別ごとに1列を持つ。1店舗には種別ごとに1つしかタグが付かないので、
 * 1行でその店舗が今どう分類されているかが分かる。
 * 先頭3列（店舗ID・階層・店舗名）の並びは雛形CSVと揃えている。
 *
 * 絞り込みは列ごとに行う。どの条件で絞っているかがヘッダーを見れば分かり、
 * 「東京都かつ未設定」のような掛け合わせも列を跨いで指定できる。
 */
export function SellerTagAssignTable({
  sellers,
  tagTypes,
  selectedTypeId,
  assignments,
  onAssign,
  onUnassign,
  onOpenImport,
  submitting,
}: SellerTagAssignTableProps) {
  // 列フィルタとは別に、店舗名などをすぐ打ち込んで探せる横断検索を上部に置く
  const [keyword, setKeyword] = useState('');
  const [textFilters, setTextFilters] = useState<Record<TextColumn, string>>({
    sellerCustomerId: '',
    hierarchy: '',
    sellerName: '',
  });
  /** タグ種別ID → 選択中のタグ名ID（UNSET を含む）。空なら絞り込みなし */
  const [tagFilters, setTagFilters] = useState<Map<string, Set<string>>>(new Map());
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [pendingValueId, setPendingValueId] = useState('');
  // タグを設定する前に、選んだ店舗が合っているかを一覧で確かめるための絞り込み
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);

  const selectedType = tagTypes.find((t) => t.id === selectedTypeId);

  const setTextFilter = (column: TextColumn, value: string) =>
    setTextFilters((prev) => ({ ...prev, [column]: value }));

  const setTagFilter = (tagTypeId: string, values: Set<string>) =>
    setTagFilters((prev) => {
      const next = new Map(prev);
      if (values.size === 0) next.delete(tagTypeId);
      else next.set(tagTypeId, values);
      return next;
    });

  /** タグ列の選択肢。店舗数を添えて、絞る前にどれだけ該当するかが分かるようにする */
  const tagFilterOptions = useMemo(() => {
    const result = new Map<string, FilterOption[]>();
    for (const tagType of tagTypes) {
      const counts = new Map<string, number>();
      let unsetCount = 0;
      for (const seller of sellers) {
        const valueId = assignments.get(seller.sellerId)?.get(tagType.id);
        if (valueId) counts.set(valueId, (counts.get(valueId) ?? 0) + 1);
        else unsetCount++;
      }
      result.set(tagType.id, [
        ...tagType.tags.map((value) => ({
          id: value.id,
          name: value.name,
          count: counts.get(value.id) ?? 0,
        })),
        { id: UNSET, name: '未設定', count: unsetCount },
      ]);
    }
    return result;
  }, [tagTypes, sellers, assignments]);

  const visibleSellers = useMemo(() => {
    return sellers.filter((seller) => {
      // 選択中のみ表示のときは、列の絞り込みより優先して選んだ店舗だけを出す
      if (showSelectedOnly) return selected.has(seller.sellerId);

      const q = keyword.trim();
      if (q && !`${seller.sellerCustomerId} ${seller.sellerName} ${seller.hierarchy}`.includes(q)) {
        return false;
      }

      for (const column of TEXT_COLUMNS) {
        const q = textFilters[column].trim();
        if (q && !seller[column].includes(q)) return false;
      }

      for (const [tagTypeId, values] of tagFilters) {
        const valueId = assignments.get(seller.sellerId)?.get(tagTypeId);
        if (!values.has(valueId ?? UNSET)) return false;
      }

      return true;
    });
  }, [sellers, keyword, textFilters, tagFilters, assignments, showSelectedOnly, selected]);

  const visibleIds = useMemo(() => visibleSellers.map((s) => s.sellerId), [visibleSellers]);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const filterCount =
    tagFilters.size +
    TEXT_COLUMNS.filter((c) => textFilters[c].trim().length > 0).length +
    (keyword.trim().length > 0 ? 1 : 0);

  const toggleAllVisible = (checked: boolean) => {
    const next = new Set(selected);
    for (const id of visibleIds) {
      if (checked) next.add(id);
      else next.delete(id);
    }
    setSelected(next);
  };

  const toggleOne = (sellerId: string, checked: boolean) => {
    const next = new Set(selected);
    if (checked) next.add(sellerId);
    else next.delete(sellerId);
    setSelected(next);
  };

  /** 選択を空に戻す。選択中のみ表示のままだと0件になるので一緒に解除する */
  const clearSelection = () => {
    setSelected(new Set());
    setShowSelectedOnly(false);
  };

  const clearAllFilters = () => {
    setKeyword('');
    setTextFilters({ sellerCustomerId: '', hierarchy: '', sellerName: '' });
    setTagFilters(new Map());
  };

  const handleAssign = () => {
    if (!pendingValueId) return;
    onAssign(Array.from(selected), pendingValueId);
    clearSelection();
  };

  const handleUnassign = () => {
    onUnassign(Array.from(selected));
    clearSelection();
  };

  return (
    <Card variant="outlined">
      <div className="flex flex-col gap-2 border-b px-4 py-2.5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Typography variant="subtitle2" fontWeight={700}>
            店舗一覧
          </Typography>
          <Button type="button" size="sm" onClick={onOpenImport}>
            <Upload className="mr-1 h-4 w-4" />
            一括登録
          </Button>
        </div>
        <TextField
          size="small"
          placeholder="店舗ID・店舗名・階層で検索"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          slotProps={{ htmlInput: { 'aria-label': '店舗を検索' } }}
          sx={{ width: '100%', maxWidth: 320 }}
        />
        <p className="text-xs text-gray-500">
          色の付いた列が編集対象です。列ごとの絞り込みは各列のアイコンから指定できます
        </p>
      </div>

      {showSelectedOnly && (
        <div className="flex flex-wrap items-center gap-2 border-b bg-gray-900 px-4 py-2 text-xs text-white">
          <span>
            選択した <b className="tabular-nums">{selected.size}</b>{' '}
            店舗だけを表示中です。設定する前に対象を確かめてください
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-white hover:bg-white/15"
            onClick={() => setShowSelectedOnly(false)}
          >
            <X className="mr-1 h-3.5 w-3.5" />
            すべて表示
          </Button>
        </div>
      )}

      {!showSelectedOnly && filterCount > 0 && (
        <div className="flex flex-wrap items-center gap-2 border-b bg-blue-50 px-4 py-2 text-xs text-blue-700">
          <span>
            <b className="tabular-nums">{filterCount}</b>列で絞り込み中 —{' '}
            <b className="tabular-nums">{visibleSellers.length}</b>件
          </span>
          <Button type="button" variant="ghost" size="sm" onClick={clearAllFilters}>
            <X className="mr-1 h-3.5 w-3.5" />
            すべての絞り込みを解除
          </Button>
        </div>
      )}

      <TableContainer sx={{ maxHeight: 520 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={allVisibleSelected}
                  indeterminate={!allVisibleSelected && visibleIds.some((id) => selected.has(id))}
                  onChange={(e) => toggleAllVisible(e.target.checked)}
                  inputProps={{ 'aria-label': '表示中の店舗をすべて選択' }}
                />
              </TableCell>
              {TEXT_COLUMNS.map((column) => (
                <TableCell key={column} sx={{ width: TEXT_COLUMN_WIDTHS[column] }}>
                  <span className="inline-flex items-center">
                    {TEXT_COLUMN_LABELS[column]}
                    <TextColumnFilter
                      label={TEXT_COLUMN_LABELS[column]}
                      value={textFilters[column]}
                      onChange={(value) => setTextFilter(column, value)}
                    />
                  </span>
                </TableCell>
              ))}
              {tagTypes.map((tagType) => {
                const active = tagType.id === selectedTypeId;
                return (
                  <TableCell
                    key={tagType.id}
                    title={active ? `${tagType.name}（編集対象の列）` : tagType.name}
                    sx={{
                      width: TAG_COLUMN_WIDTH,
                      minWidth: TAG_COLUMN_WIDTH,
                      ...(active ? { bgcolor: '#e8f1fb', color: '#1565c0', fontWeight: 700 } : {}),
                    }}
                  >
                    <span className="inline-flex items-center">
                      {tagType.name}
                      {/* 幅を動かさないよう、編集中であることは色で示して読み上げにだけ残す */}
                      {active && <span className="sr-only">（編集中）</span>}
                      <SelectColumnFilter
                        label={tagType.name}
                        options={tagFilterOptions.get(tagType.id) ?? []}
                        selected={tagFilters.get(tagType.id) ?? new Set()}
                        onChange={(values) => setTagFilter(tagType.id, values)}
                      />
                    </span>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleSellers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4 + tagTypes.length}>
                  <Typography variant="body2" color="text.secondary" align="center">
                    条件に合う店舗がありません
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              visibleSellers.map((seller) => {
                const rowTags = assignments.get(seller.sellerId);
                const checked = selected.has(seller.sellerId);
                return (
                  <TableRow key={seller.sellerId} hover selected={checked}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={checked}
                        onChange={(e) => toggleOne(seller.sellerId, e.target.checked)}
                        inputProps={{ 'aria-label': `${seller.sellerCustomerId} を選択` }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>
                      {seller.sellerCustomerId}
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontSize: 12 }}>
                      {seller.hierarchy}
                    </TableCell>
                    <TableCell>{seller.sellerName}</TableCell>
                    {tagTypes.map((tagType) => {
                      const valueId = rowTags?.get(tagType.id);
                      const valueName = tagType.tags.find((v) => v.id === valueId)?.name;
                      const active = tagType.id === selectedTypeId;

                      // 編集中の列だけクリックで変更できる。他の種別は右で選び直してから編集する
                      if (!active) {
                        return (
                          <TableCell
                            key={tagType.id}
                            sx={{ width: TAG_COLUMN_WIDTH, minWidth: TAG_COLUMN_WIDTH }}
                          >
                            {valueName ? (
                              <span className="inline-block rounded-full border border-gray-300 px-2.5 py-0.5 text-xs">
                                {valueName}
                              </span>
                            ) : (
                              <span className="inline-block rounded-full border border-dashed border-gray-300 px-2.5 py-0.5 text-xs text-gray-400">
                                未設定
                              </span>
                            )}
                          </TableCell>
                        );
                      }
                      return (
                        <TableCell
                          key={tagType.id}
                          sx={{
                            width: TAG_COLUMN_WIDTH,
                            minWidth: TAG_COLUMN_WIDTH,
                            bgcolor: '#f3f8fd',
                          }}
                        >
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                type="button"
                                disabled={submitting || tagType.tags.length === 0}
                                aria-label={`${seller.sellerName} の「${tagType.name}」を変更`}
                                className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs transition hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-60 ${
                                  valueName
                                    ? 'border-blue-600 bg-blue-50 font-medium text-blue-700'
                                    : 'border-dashed border-gray-400 text-gray-500'
                                }`}
                              >
                                {valueName ?? '未設定'}
                                <ChevronDown className="h-3 w-3 opacity-60" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" className="max-h-72 overflow-y-auto">
                              {tagType.tags.map((value) => (
                                <DropdownMenuItem
                                  key={value.id}
                                  onClick={() => onAssign([seller.sellerId], value.id)}
                                  className={
                                    value.id === valueId ? 'font-medium text-blue-700' : ''
                                  }
                                >
                                  {value.name}
                                </DropdownMenuItem>
                              ))}
                              {valueId && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={() => onUnassign([seller.sellerId])}
                                    className="text-red-600"
                                  >
                                    タグを外す
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <div className="border-t px-4 py-2 text-xs text-gray-500">
        表示中 {visibleSellers.length}件 / 全{sellers.length}件
        {selected.size > 0 && <span className="ml-2">（選択中 {selected.size}件）</span>}
      </div>

      {selected.size > 0 && selectedType && (
        <div className="sticky bottom-0 flex flex-wrap items-center gap-2 bg-gray-900 px-4 py-2.5 text-white">
          <span className="text-sm">
            <b className="tabular-nums">{selected.size}店舗</b> を選択中 ―「{selectedType.name}」を
          </span>
          <TextField
            select
            size="small"
            value={pendingValueId}
            onChange={(e) => setPendingValueId(e.target.value)}
            sx={{ minWidth: 200, bgcolor: 'white', borderRadius: 1 }}
            slotProps={{ htmlInput: { 'aria-label': 'タグ名を選ぶ' } }}
          >
            {selectedType.tags.length === 0 ? (
              <MenuItem value="" disabled>
                タグ名がありません
              </MenuItem>
            ) : (
              selectedType.tags.map((value) => (
                <MenuItem key={value.id} value={value.id}>
                  {value.name}
                </MenuItem>
              ))
            )}
          </TextField>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={!pendingValueId || submitting}
            onClick={handleAssign}
          >
            この値にする
          </Button>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={submitting}
            onClick={handleUnassign}
          >
            タグを外す
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="text-white hover:bg-white/15"
            onClick={() => setShowSelectedOnly((prev) => !prev)}
          >
            <ListFilter className="mr-1 h-3.5 w-3.5" />
            {showSelectedOnly ? 'すべて表示' : '選択中のみ表示'}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="text-white hover:bg-white/15"
            onClick={clearSelection}
          >
            選択を解除
          </Button>
        </div>
      )}
    </Card>
  );
}
